import type {
  LevelCompleteResponse,
  LevelDoubleRewardResponse,
  LevelFailedResponse,
  PurchaseItem,
  PurchaseResponse,
  ReviveOfferResponse,
  RewardBoosterResponse,
  RewardBoosterType,
  RewardHeartResponse,
} from "../api/dto/player.dto.js";
import { ApiError } from "../api/http_client.js";
import {
  APP_RUN_ID,
  completeLevel as completeLevelRequest,
  doubleLevelReward as doubleLevelRewardRequest,
  getReviveOffer as getReviveOfferRequest,
  purchaseItem as purchaseItemRequest,
  reportLevelFailed as reportLevelFailedRequest,
  reportLevelStart as reportLevelStartRequest,
  rewardBooster as rewardBoosterRequest,
  rewardHeart as rewardHeartRequest,
  type LevelStartResponse,
} from "../api/requests/economy.requests.js";
import { grantTutorialBooster as grantTutorialBoosterRequest } from "../api/requests/cumulative_boosters.requests.js";
import type { CumulativeBoosterInventoryDto, TutorialBoosterResponse } from "../api/dto/cumulative_boosters.dto.js";
import { CumulativeBoosterID } from "../cumulative_boosters/cumulative_booster_id.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { isAmbiguousNetworkFailure, isNetworkishError } from "../utils/network_errors.js";
import { OfflineJournal } from "./offline_journal.js";
import { shouldGoLocal } from "./offline_policy.js";
import { OfflineEconomy, difficultyName } from "./offline_economy.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";

// Rocket is a client-only passive booster with no rewarded-ad inventory yet.
const REWARDED_BOOSTER_INVENTORY: Partial<Record<RewardBoosterType, "fireworks" | "multi_balls" | "big_balls" | "infinite_balls" | "mines" | "freezes">> = {
  firework: "fireworks",
  five_balls: "multi_balls",
  big_ball: "big_balls",
  infinite_shots: "infinite_balls",
  mine: "mines",
  freeze: "freezes",
};

const TUTORIAL_BOOSTER_INVENTORY: Partial<Record<CumulativeBoosterID, keyof CumulativeBoosterInventoryDto>> = {
  [CumulativeBoosterID.InfiniteBalls]: "infinite_balls",
  [CumulativeBoosterID.BigBall]: "big_balls",
  [CumulativeBoosterID.Fireworks]: "fireworks",
  [CumulativeBoosterID.Multiball]: "multi_balls",
};
// Offline guess of the server's starter_pack amount; the player reload after
// the journal replay replaces it with what the server actually credited.
const OFFLINE_TUTORIAL_BOOSTER_QTY = 3;

let active_level: number | null = null;
let revive_purchases_on_attempt = 0;


function isoOrNull( ms: number ): string | null
{
  return ms > 0 ? new Date( ms ).toISOString() : null;
}

function localLevelStart( level: number ): LevelStartResponse
{
  const journal = OfflineJournal.list();
  const last = journal[journal.length - 1];
  const duplicate = last?.type === "level_start" && last.payload.level === level && last.payload.app_run_id === APP_RUN_ID;
  if ( !duplicate )
  {
    OfflineJournal.append( "level_start", { level, app_run_id: APP_RUN_ID } );
  }
  return {
    ok: true,
    attempt_number: 0,
    attempt_mass_multiplier: 1,
    attempt_mass: { multiplier: 1, source: "offline" },
  };
}

export async function reportLevelStart( level: number ): Promise<LevelStartResponse>
{
  active_level = level;
  revive_purchases_on_attempt = 0;
  CrashBreadcrumbs.add( 'level', `start ${level}${shouldGoLocal() ? ' (offline)' : ''}` );

  if ( shouldGoLocal() )
    return localLevelStart( level );

  try
  {
    return await reportLevelStartRequest( level );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelStart( level );
  }
}

function localLevelComplete( level: number, stars: number ): LevelCompleteResponse
{
  const difficulty = difficultyName( LevelPackService.getDifficulty( level ) );
  const granted = OfflineEconomy.completeLevel( level, stars, difficulty );
  OfflineJournal.append( "level_complete", { level, stars } );
  active_level = null;
  return { player: OfflineEconomy.localPlayerDto(), granted };
}

export async function completeLevel( level: number, stars: number ): Promise<LevelCompleteResponse>
{
  CrashBreadcrumbs.add( 'level', `complete ${level} stars=${stars}${shouldGoLocal() ? ' (offline)' : ''}` );
  if ( shouldGoLocal() )
    return localLevelComplete( level, stars );

  try
  {
    const response = await completeLevelRequest( level, stars );
    active_level = null;
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelComplete( level, stars );
  }
}

function localLevelFailed(): LevelFailedResponse
{
  const settled = OfflineEconomy.spendHeart();
  OfflineJournal.append( "level_failed", { level: active_level } );
  active_level = null;
  return {
    hearts: settled.hearts,
    max_hearts: settled.max_hearts,
    next_heart_at: isoOrNull( settled.next_heart_at ),
  };
}

export async function reportLevelFailed(): Promise<LevelFailedResponse>
{
  CrashBreadcrumbs.add( 'level', `failed ${active_level ?? '?'}${shouldGoLocal() ? ' (offline)' : ''}` );
  if ( shouldGoLocal() )
    return localLevelFailed();

  try
  {
    const response = await reportLevelFailedRequest();
    active_level = null;
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelFailed();
  }
}

function localReviveOffer(): ReviveOfferResponse
{
  const level = active_level ?? StatisticsStorage.load().level;
  return OfflineEconomy.reviveOffer( level, revive_purchases_on_attempt );
}

export async function getReviveOffer(): Promise<ReviveOfferResponse>
{
  if ( shouldGoLocal() )
    return localReviveOffer();

  try
  {
    return await getReviveOfferRequest();
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localReviveOffer();
  }
}

function localPurchase(
  item: PurchaseItem,
  expected?: { purchase_index?: number; price: number; qty: number }
): PurchaseResponse
{
  const economy = OfflineEconomy.getEconomyConfig();
  const stats = StatisticsStorage.load();
  let price: number;
  let qty: number;
  let purchase_index: number | undefined;

  if ( item === "continue_balls" )
  {
    const offer = localReviveOffer();
    if ( !offer.available || !offer.offer )
      throw new ApiError( 409, "revive_unavailable" );
    if ( expected?.purchase_index !== undefined && expected.purchase_index !== offer.purchase_index )
      throw new ApiError( 409, "revive_offer_changed" );
    price = offer.offer.price;
    qty = offer.offer.shots;
    purchase_index = offer.purchase_index;
  }
  else if ( item === "fireworks" )
  {
    price = stats.fireworks_price || economy.fireworks_price;
    qty = stats.fireworks_pack_qty || economy.fireworks_pack_qty;
  }
  else
  {
    price = stats.heart_refill_price || economy.heart_refill_price;
    qty = 1;
  }

  if ( expected && ( expected.price !== price || expected.qty !== qty ) )
    throw new ApiError( 409, `${item}_offer_changed` );

  const result = OfflineEconomy.purchase( item, price, qty );
  if ( !result.ok )
    throw new ApiError( 409, result.reason );

  OfflineJournal.append( "purchase", { item, price, qty, ...( purchase_index !== undefined ? { purchase_index } : {} ) } );
  if ( item === "continue_balls" )
    revive_purchases_on_attempt++;

  return {
    player: OfflineEconomy.localPlayerDto(),
    purchased: {
      item,
      price,
      qty,
      ...( purchase_index !== undefined
        ? { purchase_index, remaining_purchases: Math.max( 0, localReviveOffer().remaining_purchases ) }
        : {} ),
    },
  };
}

export async function purchaseItem(
  item: PurchaseItem,
  expected?: { purchase_index?: number; price: number; qty: number }
): Promise<PurchaseResponse>
{
  if ( shouldGoLocal() )
    return localPurchase( item, expected );

  try
  {
    const response = await purchaseItemRequest( item, expected );
    if ( item === "continue_balls" )
      revive_purchases_on_attempt++;
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) || isAmbiguousNetworkFailure( e ) ) throw e;
    return localPurchase( item, expected );
  }
}

function localDoubleReward( level: number, stars: number ): LevelDoubleRewardResponse
{
  const difficulty = difficultyName( LevelPackService.getDifficulty( level ) );
  const coins = OfflineEconomy.doubleLevelReward( level, stars, difficulty );
  OfflineJournal.append( "level_double_reward", { level, stars } );
  return { player: OfflineEconomy.localPlayerDto(), granted: { coins, points: 0 } };
}

export async function doubleLevelReward( level: number, stars: number ): Promise<LevelDoubleRewardResponse>
{
  if ( shouldGoLocal() )
    return localDoubleReward( level, stars );

  try
  {
    return await doubleLevelRewardRequest( level, stars );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localDoubleReward( level, stars );
  }
}

function localRewardHeart(): RewardHeartResponse
{
  const stats = StatisticsStorage.load();
  if ( stats.hearts >= stats.max_hearts )
    throw new ApiError( 409, "hearts_full" );
  OfflineEconomy.addHearts( 1 );
  OfflineJournal.append( "reward_heart", {} );
  return { player: OfflineEconomy.localPlayerDto() };
}

export async function rewardHeart(): Promise<RewardHeartResponse>
{
  if ( shouldGoLocal() )
    return localRewardHeart();

  try
  {
    return await rewardHeartRequest();
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) || isAmbiguousNetworkFailure( e ) ) throw e;
    return localRewardHeart();
  }
}

function localRewardBooster( booster_type: RewardBoosterType, placement: string, ad_id: string ): RewardBoosterResponse
{
  const inventory_field = REWARDED_BOOSTER_INVENTORY[booster_type];
  if ( inventory_field )
    OfflineEconomy.addBoosters( { [ inventory_field ]: 1 } );
  OfflineJournal.append( "reward_booster", { booster_type, placement, ad_id } );
  return {
    player: OfflineEconomy.localPlayerDto(),
    granted: { booster_type, quantity: 1, placement, ad_id },
    idempotent: false,
  };
}

export async function rewardBooster(
  booster_type: RewardBoosterType,
  placement: string,
  ad_id: string
): Promise<RewardBoosterResponse>
{
  if ( shouldGoLocal() )
    return localRewardBooster( booster_type, placement, ad_id );

  try
  {
    return await rewardBoosterRequest( booster_type, placement, ad_id );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localRewardBooster( booster_type, placement, ad_id );
  }
}

function localTutorialBooster( booster_id: CumulativeBoosterID ): TutorialBoosterResponse
{
  const inventory_field = TUTORIAL_BOOSTER_INVENTORY[booster_id];
  if ( inventory_field )
    OfflineEconomy.addBoosters( { [ inventory_field ]: OFFLINE_TUTORIAL_BOOSTER_QTY } );
  OfflineJournal.append( "booster_tutorial", { booster_id } );
  return {
    player: OfflineEconomy.localPlayerDto(),
    granted: { booster_id, quantity: inventory_field ? OFFLINE_TUTORIAL_BOOSTER_QTY : 0 },
    idempotent: false,
  };
}

/** Credits the boosters a booster's unlock tutorial hands out (once per booster, server-side). */
export async function grantTutorialBooster( booster_id: CumulativeBoosterID ): Promise<TutorialBoosterResponse>
{
  if ( shouldGoLocal() )
    return localTutorialBooster( booster_id );

  try
  {
    return await grantTutorialBoosterRequest( booster_id );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localTutorialBooster( booster_id );
  }
}
